import React, { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { COLLECTIONS } from "@/data/content";

export default function LuxuryCollections({ onExplore }) {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const w = scrollRef.current.offsetWidth;
    scrollRef.current.scrollBy({ left: dir * (w * 0.6), behavior: "smooth" });
  };

  return (
    <section
      id="luxury"
      className="py-24 md:py-32 bg-gradient-to-b from-[#F8F9FB] to-white"
      data-testid="luxury-collections-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-[#C9A227]" />
              <span className="overline">Luxury Collections</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A2342] max-w-2xl">
              Hand-picked collections for every chapter of life.
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll(-1)}
              data-testid="collections-prev"
              aria-label="Previous"
              className="w-12 h-12 rounded-full border border-gray-200 hover:bg-[#0A2342] hover:text-white hover:border-[#0A2342] text-[#0A2342] flex items-center justify-center transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll(1)}
              data-testid="collections-next"
              aria-label="Next"
              className="w-12 h-12 rounded-full border border-gray-200 hover:bg-[#0A2342] hover:text-white hover:border-[#0A2342] text-[#0A2342] flex items-center justify-center transition-all"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto no-scrollbar px-6 lg:px-10 scroll-smooth snap-x"
      >
        {COLLECTIONS.map((c, i) => (
          <motion.a
            key={c.name}
            href="#packages"
            onClick={onExplore}
            data-testid={`collection-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.06 }}
            className="snap-start shrink-0 relative h-[440px] w-[80vw] md:w-[420px] rounded-3xl overflow-hidden group cursor-pointer"
          >
            <img
              src={c.image}
              alt={c.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7 text-white">
              <div className="text-[#C9A227] text-xs uppercase tracking-[0.22em] mb-2">{c.count}</div>
              <div className="flex items-end justify-between gap-3">
                <h3 className="font-display text-3xl leading-tight">{c.name}</h3>
                <span className="w-11 h-11 rounded-full glass flex items-center justify-center text-[#0A2342] -translate-x-0 group-hover:-translate-y-1 transition-transform duration-500">
                  <ArrowRight className="w-5 h-5" />
                </span>
              </div>
            </div>
          </motion.a>
        ))}
        <div className="shrink-0 w-6" />
      </div>
    </section>
  );
}
